import React from "react";

/**
 * Visual diagram / workflow guide for Admin Design Studio.
 * Shows 8 numbered steps with CAD-style connecting lines.
 */

const STEPS = [
  {
    n: "01",
    title: "NAME & TYPE",
    text: "Start by entering a design name (e.g. RAW HOODIE 001) and pick the clothing type (Hoodie, T-Shirt, Jumper, Pants, Jacket…). The type controls which panels and preset measurements are available.",
    testid: "field-name",
  },
  {
    n: "02",
    title: "DESCRIBE & COLOR",
    text: "Write a short description and pick a base colour. The colour tints the CAD wireframe preview and is included in the factory spec.",
    testid: "field-description",
  },
  {
    n: "03",
    title: "AI IMAGE GENERATION",
    text: "Type a detailed prompt — e.g. 'oversized black hoodie with red stitching, minimalist streetwear' — and click GENERATE WITH NANO BANANA. The AI produces a studio-quality product photo. You can also upload your own image instead.",
    testid: "ai-prompt",
  },
  {
    n: "04",
    title: "APPLY DIMENSION PRESET",
    text: "Click APPLY {TYPE} PRESET to auto-fill industry-standard S / M / L / XL measurements in centimetres. No more working measurements out yourself. Use AI REFINE for custom tweaks based on your description.",
    testid: "load-preset-dims",
  },
  {
    n: "05",
    title: "LOAD MATERIAL PANELS",
    text: "Click LOAD PANELS — every garment panel (Body, Hood, Sleeves, Cuffs, Hem, Pockets…) is listed automatically. Assign a fabric to each panel from the built-in fabric library (French Terry, Denim, Cotton Jersey, YKK zips, Corozo buttons, etc.) or type your own.",
    testid: "load-panel-preset",
  },
  {
    n: "06",
    title: "SET PRICE & STOCK",
    text: "Enter the price in GBP (£) and the starting stock count. Tick PUBLISH TO SHOP to make the product visible to customers. Leave unticked to keep it as a draft.",
    testid: "field-price",
  },
  {
    n: "07",
    title: "AI FACTORY NOTES",
    text: "Click AI GENERATE on the Construction Notes section. GPT writes full factory-ready notes: stitching, seams, fabric GSM, trims, labels, packaging, tolerances and QC. Edit them manually afterwards if you need to.",
    testid: "ai-factory-notes",
  },
  {
    n: "08",
    title: "SAVE & EXPORT TO FACTORY",
    text: "Click SAVE. Then use PDF ↓ to download a branded RAW COMPANY tech-pack (image, dimensions table, material panels, factory notes) or JSON ↓ for digital factory systems. Email the file to your factory and they have everything they need to produce the garment.",
    testid: "export-pdf",
  },
];

const HelpDiagram = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm overflow-y-auto"
      data-testid="help-modal"
      onClick={onClose}
    >
      <div className="min-h-full py-10 px-4 flex items-start justify-center">
        <div
          className="w-full max-w-5xl border border-zinc-800 bg-[#050505] relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#050505] border-b border-zinc-800 px-8 py-5 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff3333] mb-1">
                // ADMIN_MANUAL.V1
              </div>
              <h2 className="font-display text-2xl sm:text-3xl leading-none">
                HOW TO DESIGN A GARMENT
              </h2>
            </div>
            <button
              data-testid="help-close"
              onClick={onClose}
              className="w-10 h-10 border border-zinc-700 text-zinc-400 hover:border-[#ff3333] hover:text-[#ff3333] transition-colors flex items-center justify-center text-xl"
            >
              ×
            </button>
          </div>

          {/* Diagram grid */}
          <div className="p-6 sm:p-8">
            <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-8 border-l-2 border-[#ff3333] pl-3">
              Follow this 8-step workflow to go from idea → factory-ready tech-pack in minutes.
            </p>

            <div className="relative">
              {/* Vertical connector line (desktop only) */}
              <div
                className="hidden md:block absolute left-[39px] top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-zinc-800 to-transparent"
                aria-hidden
              />

              <div className="space-y-6">
                {STEPS.map((s, i) => (
                  <div
                    key={s.n}
                    className="relative grid grid-cols-[80px_1fr] gap-4 sm:gap-6 items-start"
                  >
                    {/* Step number badge */}
                    <div className="relative z-10">
                      <div className="w-20 h-20 border-2 border-[#ff3333] bg-[#0a0a0a] flex items-center justify-center font-display text-2xl text-[#ff3333]">
                        {s.n}
                      </div>
                    </div>

                    {/* Step content */}
                    <div className="border border-zinc-800 bg-[#0a0a0a] p-5 hover:border-[#ff3333] transition-colors">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="font-display text-lg sm:text-xl">{s.title}</div>
                        <div className="flex-1 h-px bg-zinc-800" />
                        <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-600 font-mono">
                          STEP {i + 1}/8
                        </div>
                      </div>
                      <p className="text-sm text-zinc-300 leading-relaxed font-mono">
                        {s.text}
                      </p>
                      <div className="mt-3 text-[9px] tracking-[0.3em] uppercase text-zinc-600 font-mono">
                        // LOCATE: data-testid="{s.testid}"
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Flow summary */}
            <div className="mt-10 border border-zinc-800 bg-[#0a0a0a] p-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff3333] mb-3">
                // QUICK_REFERENCE
              </div>
              <div className="font-mono text-xs text-zinc-400 leading-loose overflow-x-auto">
                <pre className="whitespace-pre">
{`NAME  →  TYPE  →  AI_IMAGE  →  DIM_PRESET  →  PANELS  →  FABRICS  →  PRICE  →  NOTES  →  SAVE  →  PDF/JSON
  ↓       ↓        ↓            ↓            ↓         ↓           ↓        ↓        ↓       ↓
 "01"   "01"     "03"         "04"         "05"      "05"        "06"     "07"     "08"    "08"`}
                </pre>
              </div>
            </div>

            {/* Tips */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  t: "TIP / PRESETS",
                  b: "Measurements are in CM. Always apply the preset first, then refine with AI — never start from zeros.",
                },
                {
                  t: "TIP / MATERIALS",
                  b: "Each panel can use a different fabric. The factory reads this to source correct materials per cut-piece.",
                },
                {
                  t: "TIP / PAYMENTS",
                  b: "Prices are in GBP (£). Currently running in Stripe TEST mode — use card 4242 4242 4242 4242 to test.",
                },
              ].map((x) => (
                <div key={x.t} className="border-l-2 border-[#ff3333] pl-4">
                  <div className="text-[9px] tracking-[0.3em] uppercase text-[#ff3333] mb-1 font-bold">
                    {x.t}
                  </div>
                  <div className="text-xs text-zinc-400 leading-relaxed">{x.b}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-zinc-800 px-8 py-4 text-center">
            <button
              data-testid="help-close-footer"
              onClick={onClose}
              className="btn-raw btn-raw-primary"
            >
              → GOT IT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpDiagram;
